// Single owner of every persisted agent-chat UI preference.
//
// Each preference has one runtime owner (the live React state in the chat
// widgets); this module is the persistence layer those states read once at
// mount/hydration and write through on change. It replaces the former mix of
// ad-hoc localStorage keys and `?chat` / `?chatPinned` URL parameters: the
// URL now carries only conversation identity (`thread`), and every user
// preference lives here.

export type AgentChatDock = 'bottom' | 'left' | 'right' | 'floating';

export type AgentChatPlacement = {
  dock: AgentChatDock;
  position: { x: number; y: number };
};

const KEYS = {
  open: 'agentChatOpen',
  pinned: 'agentChatPinned',
  sideWidth: 'agentChatSideWidth',
  placement: 'agentChatPlacement',
  floatingSize: 'agentFloatingSize',
  floatingMaximized: 'agentFloatingMaximized',
  sidebarWidth: 'agentFloatingSidebarWidth',
  threadTitles: 'agentThreadTitles',
  subjectDrafts: 'agentChatSubjectDrafts',
} as const;

const DEFAULT_PLACEMENT: AgentChatPlacement = {
  dock: 'bottom',
  position: { x: 24, y: 100 },
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable; the value still applies for this session.
  }
};

// --- Chat open/closed (floating mode) ---

/** Stored open/closed preference, or null when the user has never chose. */
export const readChatOpen = (): boolean | null => {
  const stored = read(KEYS.open);
  if (stored === 'true') {
    return true;
  }
  if (stored === 'false') {
    return false;
  }
  return null;
};

export const writeChatOpen = (open: boolean) => write(KEYS.open, String(open));

// --- Pin (dock the chat to the page side) ---

export const readChatPinned = (): boolean => read(KEYS.pinned) !== 'false';

export const writeChatPinned = (pinned: boolean) =>
  write(KEYS.pinned, String(pinned));

// --- Side-dock width ---

/** Stored preferred side-dock width in px, or null when never stored. */
export const readChatSideWidth = (): number | null => {
  const width = Number(read(KEYS.sideWidth));
  return Number.isFinite(width) && width >= 360 ? width : null;
};

export const writeChatSideWidth = (width: number) =>
  write(KEYS.sideWidth, String(width));

// --- Dock placement ---

export const readChatPlacement = (): AgentChatPlacement => {
  try {
    const stored: unknown = JSON.parse(read(KEYS.placement) || 'null');
    if (
      stored &&
      typeof stored === 'object' &&
      'dock' in stored &&
      typeof stored.dock === 'string' &&
      'position' in stored &&
      stored.position !== null &&
      typeof stored.position === 'object' &&
      'x' in stored.position &&
      'y' in stored.position &&
      typeof stored.position.x === 'number' &&
      typeof stored.position.y === 'number' &&
      (['bottom', 'left', 'right', 'floating'] as string[]).includes(
        stored.dock,
      ) &&
      Number.isFinite(stored.position?.x) &&
      Number.isFinite(stored.position?.y)
    ) {
      return {
        dock: stored.dock as AgentChatDock,
        position: { x: stored.position.x, y: stored.position.y },
      };
    }
  } catch {
    return DEFAULT_PLACEMENT;
  }
  return DEFAULT_PLACEMENT;
};

export const writeChatPlacement = (placement: AgentChatPlacement) =>
  write(KEYS.placement, JSON.stringify(placement));

// --- Floating popup size ---

export type AgentChatFloatingSize = { width?: number; height?: number };

export const readChatFloatingSize = (): AgentChatFloatingSize | null => {
  const stored = read(KEYS.floatingSize);
  if (!stored) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      (!('width' in parsed) || isFiniteNumber(parsed.width)) &&
      (!('height' in parsed) || isFiniteNumber(parsed.height))
    ) {
      return parsed as AgentChatFloatingSize;
    }
  } catch {
    return null;
  }
  return null;
};

export const writeChatFloatingSize = (size: AgentChatFloatingSize) =>
  write(KEYS.floatingSize, JSON.stringify(size));

// --- Floating maximized ---

export const readChatFloatingMaximized = (): boolean =>
  read(KEYS.floatingMaximized) === 'true';

export const writeChatFloatingMaximized = (maximized: boolean) =>
  write(KEYS.floatingMaximized, String(maximized));

// --- Floating sidebar width ---

/** Stored floating-sidebar width in px, or null when never stored. */
export const readChatSidebarWidth = (): number | null => {
  try {
    const stored = read(KEYS.sidebarWidth);
    const parsed: unknown = stored ? JSON.parse(stored) : undefined;
    return isFiniteNumber(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeChatSidebarWidth = (width: number) =>
  write(KEYS.sidebarWidth, JSON.stringify(width));

// --- Thread title overrides (client-side rename overlay) ---

export const readChatThreadTitles = (): Record<string, string> => {
  try {
    const parsed: unknown = JSON.parse(read(KEYS.threadTitles) || '{}');
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed) ||
      !Object.values(parsed).every((value) => typeof value === 'string')
    ) {
      return {};
    }
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
};

export const writeChatThreadTitles = (titles: Record<string, string>) =>
  write(KEYS.threadTitles, JSON.stringify(titles));

// --- Selected thread pointers (per subject) ---

// One map for the whole app: subjectKey → the thread the chat falls back
// to for that subject. Keys are "{type}:{id}" for entity subjects and
// "general" for patient-less threads. The legacy single-pointer slot is
// migrated once on first read.
export type AgentChatThreadSubjectKey = string;

const THREAD_POINTERS_KEY = 'agentChatThreadPointers';
const LEGACY_THREAD_POINTER_KEY = 'agentChatThreadPointer';

const migrateLegacyPointer = (pointers: Record<string, string>) => {
  const stored = read(LEGACY_THREAD_POINTER_KEY);
  if (!stored) {
    return pointers;
  }
  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      parsed &&
      typeof parsed === 'object' &&
      'patientID' in parsed &&
      'threadID' in parsed &&
      typeof (parsed as { threadID: unknown }).threadID === 'string' &&
      (parsed as { threadID: string }).threadID
    ) {
      const legacy = parsed as { patientID: string | null; threadID: string };
      const key = legacy.patientID ? `patient:${legacy.patientID}` : 'general';
      if (!pointers[key]) {
        pointers[key] = legacy.threadID;
      }
    }
  } catch {
    // Unparseable legacy pointer: drop it.
  }
  try {
    localStorage.removeItem(LEGACY_THREAD_POINTER_KEY);
  } catch {
    // Storage unavailable; the map still works for this session.
  }
  return pointers;
};

export const readChatThreadPointers = (): Record<string, string> => {
  let pointers: Record<string, string> = {};
  try {
    const parsed: unknown = JSON.parse(read(THREAD_POINTERS_KEY) || '{}');
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Object.values(parsed).every((value) => typeof value === 'string')
    ) {
      pointers = parsed as Record<string, string>;
    }
  } catch {
    pointers = {};
  }
  return migrateLegacyPointer(pointers);
};

export const writeChatThreadPointer = (
  subjectKey: AgentChatThreadSubjectKey,
  threadID: string,
) => {
  const pointers = readChatThreadPointers();
  pointers[subjectKey] = threadID;
  try {
    localStorage.setItem(THREAD_POINTERS_KEY, JSON.stringify(pointers));
  } catch {
    // Storage unavailable; the value still applies for this session.
  }
};

// --- Subject drafts ---

// Unsent composer text, banked per subject key ("{type}:{id}" or
// "general"). The chat pane is a different component instance on patient
// pages than on entity pages, so the bank must survive unmounts. Cleared
// on logout: the drafts carry typed user content.
export const readChatSubjectDrafts = (): Record<string, string> => {
  try {
    const parsed: unknown = JSON.parse(read(KEYS.subjectDrafts) || '{}');
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Object.values(parsed).every((value) => typeof value === 'string')
    ) {
      return parsed as Record<string, string>;
    }
  } catch {
    // Unparseable bank: start clean.
  }
  return {};
};

export const writeChatSubjectDraft = (subjectKey: string, draft: string) => {
  const drafts = readChatSubjectDrafts();
  if (draft) {
    drafts[subjectKey] = draft;
  } else {
    delete drafts[subjectKey];
  }
  try {
    localStorage.setItem(KEYS.subjectDrafts, JSON.stringify(drafts));
  } catch {
    // Storage unavailable; the value still applies for this session.
  }
};

// --- Logout cleanup ---

/** Removes every persisted chat preference. Thread titles and the thread
 * pointers carry user content and patient/thread identifiers: they must not
 * outlive the session that created them. */
export const clearAgentChatPreferences = () => {
  for (const key of [
    ...Object.values(KEYS),
    THREAD_POINTERS_KEY,
    LEGACY_THREAD_POINTER_KEY,
  ]) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable; nothing to clean.
    }
  }
};
